<template>
  <NModal
    :bordered="false"
    class="cb-add-component-modal"
    :onEsc="close"
    preset="card"
    show
    size="huge"
    :style="{
      '--n-border-radius': '16px',
      '--n-padding-top': '16px',
      '--n-padding-right': '16px',
      '--n-padding-bottom': '16px',
      '--n-padding-left': '16px',
    }"
    :title="title"
    :z-index="zIndex"
    @close="close()"
    @mask-click="close()"
  >
    <NCard
      aria-modal="true"
      :bordered="false"
      class="cb-add-component-modal__card"
      embedded
      role="dialog"
      :style="{
        '--n-padding-top': 0,
        '--n-padding-right': 0,
        '--n-padding-bottom': 0,
        '--n-padding-left': 0,
      }"
    >
      <template #header>
        <!-- Search and filter -->
        <div class="cb-add-component-modal__card-header" :style="{ backgroundColor: themeVars.base }">
          <NInput
            ref="inputRef"
            v-model:value="search"
            class="mb-4"
            :placeholder="t('canvasUI.canvasBuilder.searchPlaceholder')"
          >
            <template #prefix>
              <CIcon icon="search" size="16px" />
            </template>
          </NInput>
          <!--          <div v-if="!hideApps" class="flex gap-2 pb-4 uppercase tracking-wider">-->
          <!--            <CTag-->
          <!--              :bordered="activeFilter === ComponentFilterTypesEnum.Basic"-->
          <!--              class="text-xs cursor-pointer"-->
          <!--              :closable="activeFilter === ComponentFilterTypesEnum.Basic"-->
          <!--              round-->
          <!--              :trigger-click-on-close="false"-->
          <!--              @click="handleClick(ComponentFilterTypesEnum.Basic)"-->
          <!--              @close="handleClose"-->
          <!--            >-->
          <!--              {{ t('canvasUI.canvasBuilder.components.categories.standard') }}-->
          <!--            </CTag>-->
          <!--            <CTag-->
          <!--              :bordered="activeFilter === ComponentFilterTypesEnum.Apps"-->
          <!--              class="text-xs cursor-pointer"-->
          <!--              :closable="activeFilter === ComponentFilterTypesEnum.Apps"-->
          <!--              round-->
          <!--              :trigger-click-on-close="false"-->
          <!--              @click="handleClick(ComponentFilterTypesEnum.Apps)"-->
          <!--              @close="handleClose"-->
          <!--            >-->
          <!--              {{ t('canvasUI.canvasBuilder.components.categories.apps') }}-->
          <!--            </CTag>-->
          <!--          </div>-->
        </div>
        <div class="flex items-center justify-between pa-4 gap-4">
          <span class="text-sm">{{ t('canvasUI.canvasBuilder.components.name') }} ({{ items?.length }})</span>
          <div class="flex items-center grow-0 basis-50">
            <span class="text-sm whitespace-nowrap">{{ t('canvasUI.canvasBuilder.components.sortBy') }}:</span>
            <CSelect
              class="select-none"
              :on-update:value="activeSortOptionChanged"
              :options="sortOptions"
              :value="activeSortOption"
            />
          </div>
        </div>
      </template>

      <!--      <CComponentListItem-->
      <!--        v-for="selection of items"-->
      <!--        :key="selection.id"-->
      <!--        class="mx-2 mb-2 cursor-pointer"-->
      <!--        :description="selection.description"-->
      <!--        :icon="selection.icon"-->
      <!--        :title="selection.title"-->
      <!--        :type="selection.type"-->
      <!--        @click="onAdd(selection)"-->
      <!--      />-->
    </NCard>
  </NModal>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { NCard, NInput, NModal, type SelectOption } from 'naive-ui'
import { useI18n } from 'vue-i18n'
// import { orderBy } from 'lodash-es'
// import type { ComponentTypes } from '@canvas-builder/types/canvas'
// import CComponentListItem from '@lib/components/CComponentListItem/CComponentListItem.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
// import type { AppFile } from '@lib/types/app'
// import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import { isMobile } from '@lib/util/userAgents'
import { useApplicationBlockSelectorModal } from '../../composables/application-block-selector-modal.use'
import { ContentType } from '@agenda-selector/constants'

defineProps<{ items: []; zIndex?: number }>()

type ComponentSortValue = 'name-a-z' | 'name-z-a' | 'last-used'

const { close, activeElement } = useApplicationBlockSelectorModal()

const title = computed(() => {
  switch (activeElement.value) {
    case ContentType.Block:
      return t('canvasUI.agendaSelector.contentType.block')
    case ContentType.Application:
      return t('canvasUI.agendaSelector.contentType.application')
    case ContentType.Collection:
      return t('canvasUI.agendaSelector.contentType.collection')
    default:
      return ''
  }
})

// const enum ComponentFilterTypesEnum {
//   All = 'all',
//   Apps = 'apps',
//   Basic = 'basic',
// }

// interface ComponentItem {
//   app?: AppFile
//   icon?: { name: string; type?: FontAwesomeType }
//   description?: string
//   id: string
//   keywords?: string[]
//   lastUsed: string | undefined
//   title: string
//   type: ComponentTypes
// }

const LOCAL_STORAGE_KEYS = {
  preferredSorting: 'cb_preferred_sorting',
  lastUsedComponentTimestamps: 'cb_last_used_component_timestamps',
}

const { t } = useI18n()
const themeVars = useThemeVars()
const inputRef = ref<HTMLInputElement | null>(null)

const activeSortOptionChanged = (value: ComponentSortValue) => {
  activeSortOption.value = value
  localStorage.setItem(LOCAL_STORAGE_KEYS.preferredSorting, value)
}

// const activeFilter = ref(ComponentFilterTypesEnum.All)
const sortOptions = ref<SelectOption[]>([
  {
    label: t('canvasUI.canvasBuilder.components.sortOptions.lastUsed'),
    value: 'last-used',
  },
  {
    label: t('canvasUI.canvasBuilder.components.sortOptions.nameAscending'),
    value: 'name-a-z',
  },
  {
    label: t('canvasUI.canvasBuilder.components.sortOptions.nameDescending'),
    value: 'name-z-a',
  },
])

const preferredSorting = (localStorage.getItem(LOCAL_STORAGE_KEYS.preferredSorting) as ComponentSortValue | null) ?? ''

const activeSortOption = ref<ComponentSortValue>(preferredSorting || 'last-used')
const search = ref('')

// const reorderComponentsList = (items: ComponentItem[]) => {
//   switch (activeSortOption.value) {
//     case 'name-a-z':
//       return orderBy(items, [(item) => item.title.toLowerCase()], ['asc'])
//     case 'name-z-a':
//       return orderBy(items, [(item) => item.title.toLowerCase()], ['desc'])
//     case 'last-used':
//       return orderBy(items, [(item) => item.lastUsed === undefined, 'lastUsed', 'title'], ['asc', 'desc', 'asc'])
//     default:
//       return items
//   }
// }

// const onAdd = (selection: ComponentItem) => {
//   if (!selection) return
//
//   // todo add component
//
//   // scrollToComponentById(justAddedComponentId.value)
//
//   close()
// }

// const handleClick = (value: ComponentFilterTypesEnum) => {
//   activeFilter.value = activeFilter.value === value ? ComponentFilterTypesEnum.All : value
// }
//
// const handleClose = () => {
//   activeFilter.value = ComponentFilterTypesEnum.All
// }

onMounted(() => {
  nextTick(() => {
    !isMobile && inputRef.value?.focus()
  })
})
</script>

<style lang="scss">
.cb-add-component-modal {
  width: 720px;
  height: clamp(350px, calc(100vh - 120px), 800px);
}

.cb-add-component-modal__category {
  .n-collapse-item {
    .n-collapse-item__content-wrapper {
      .n-collapse-item__content-inner {
        padding: 0;
      }
    }
  }
}

.cb-add-component-modal__card .n-card__content {
  max-height: clamp(100px, calc(100vh - 360px), 560px);
  overflow: auto;
}
</style>
